// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { ClockPlus, MoreHorizontal, Pencil, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import {
  type CutOffTimeEntry,
  type CutOffTimeScope,
  type CutOffTimesSortBy,
  type CutOffTimesTarget,
  type CutOffUseCase,
  toChainSortBy,
  useCutOffTimesQuery,
} from "@/api/hooks/use-cut-off-times";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { FilterSelect } from "@/components/shared/filter-select";
import { RowId } from "@/components/shared/row-id";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { cn } from "@/lib/utils";
import { CreateCutOffTimeDialog } from "./create-cut-off-time-dialog";
import { formatCutOff, parseDays } from "./cut-off-times";
import { DeleteCutOffTimeDialog } from "./delete-cut-off-time-dialog";

interface CutOffTimesTabProps {
  /**
   * A store tab lists the store's own cut-offs and, for anyone holding the chain view grant, the
   * chain-wide ones it inherits — so the Scope column and filter appear only when both kinds can
   * show up. A chain tab lists only the chain's own rows, where scope is a constant.
   */
  target: CutOffTimesTarget;
  initialFilters?: string;
}

const FILTER_DEFAULTS = {
  search: "",
  scope: "",
  useCase: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "cutOffTime",
  sortDesc: false,
};

// Same store-vs-chain palette the Settings tab uses for its override source — identical semantics.
const SCOPE_STYLES: Record<string, string> = {
  store: "bg-[#FFF5D1] border-amber-200 text-foreground dark:bg-amber-950 dark:text-amber-300 dark:border-amber-900",
  chain: "bg-[#E3F1FF] border-blue-200 text-foreground dark:bg-blue-950 dark:text-blue-300 dark:border-blue-900",
};

// Keyed off the raw use_case so the colour is stable across languages. Deliberately avoids the
// blue/amber pair the scope column uses, so neighbouring pills stay tellable apart.
const USE_CASE_STYLES: Record<string, string> = {
  reporting: "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-900",
  impact:
    "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-900",
};

const SCOPES: CutOffTimeScope[] = ["store", "chain"];
const USE_CASES: CutOffUseCase[] = ["reporting", "impact"];

export function CutOffTimesTab({ target, initialFilters }: CutOffTimesTabProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isStore = target.kind === "store";
  // Each kind of row carries its own grant, so someone can manage a store's overrides without being
  // trusted to change a chain-wide default — the store page mixes both kinds, hence the split.
  const canEditStoreRows = hasPermission(PAGE_PERMISSIONS.storesCutOffTimesEdit);
  const canEditChainRows = hasPermission(PAGE_PERMISSIONS.chainsCutOffTimesEdit);
  // Without the chain view grant the API omits inherited rows, so the list is store-only and the
  // Scope column and filter would just repeat one value.
  const showsChainRows = isStore ? hasPermission(PAGE_PERMISSIONS.chainsCutOffTimes) : true;
  const showScope = isStore && showsChainRows;
  // Creating from a store page makes a store row; from a chain page, a chain-wide one.
  const canCreate = isStore ? canEditStoreRows : canEditChainRows;
  const canEditAnyRow = isStore ? canEditStoreRows || (showsChainRows && canEditChainRows) : canEditChainRows;
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CutOffTimeEntry | null>(null);
  const [deleting, setDeleting] = useState<CutOffTimeEntry | null>(null);
  const initial = useMemo(() => decodeFilters(initialFilters, FILTER_DEFAULTS), [initialFilters]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  // Seeded rather than left empty: the query sorts by this whether or not the state says so, and
  // with enableSortingRemoval off there is no unsorted state to go back to — so an empty state
  // would just show every header as unsorted while the rows came back sorted.
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [scope, setScope] = useState(initial.scope);
  const [useCase, setUseCase] = useState(initial.useCase);

  const isFiltered = !!(search || (showScope && scope) || useCase);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setScope("");
    setUseCase("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters });

  // Both route pages pass `target` as an inline object literal, so it is a fresh identity on every
  // parent render. Depending on the object itself made this callback — and with it the sync effect —
  // churn on every parent render, re-asserting `t: "cut-off-times"` and yanking the user back to
  // this tab whenever they tried to leave it. Depend on the primitives instead.
  const targetKind = target.kind;
  const targetName = target.kind === "store" ? target.storeName : target.chainName;

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const current = {
        search,
        scope,
        useCase,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        sortId: sorting[0]?.id ?? "cutOffTime",
        sortDesc: sorting[0]?.desc ?? false,
        ...overrides,
      };
      const encoded = encodeFilters(current, FILTER_DEFAULTS);
      const search_ = { t: "cut-off-times", ...(encoded ? { f: encoded } : {}) };
      if (targetKind === "store") {
        navigate({ to: "/stores/$storeName", params: { storeName: targetName }, search: search_, replace: true });
      } else {
        navigate({ to: "/chains/$chainName", params: { chainName: targetName }, search: search_, replace: true });
      }
    },
    [search, scope, useCase, pagination.pageIndex, pagination.pageSize, sorting, navigate, targetKind, targetName],
  );

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "cutOffTime") as CutOffTimesSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const { data, isLoading, refetch, isFetching } = useCutOffTimesQuery({
    target,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    scope: isStore ? (scope as CutOffTimeScope) || undefined : undefined,
    useCase: (useCase as CutOffUseCase) || undefined,
  });

  const handleExport = useCallback(async () => {
    const query = {
      page: 1,
      limit: -1,
      search: search || undefined,
      sortBy,
      orderBy,
      "filter[useCase]": (useCase as CutOffUseCase) || undefined,
    };
    const res =
      target.kind === "store"
        ? await api.GET("/v1/stores/{store_name}/cut-off-times", {
            params: {
              path: { store_name: target.storeName },
              query: { ...query, "filter[scope]": (scope as CutOffTimeScope) || undefined },
            },
          })
        : await api.GET("/v1/chains/{chain_name}/cut-off-times", {
            params: { path: { chain_name: target.chainName }, query: { ...query, sortBy: toChainSortBy(sortBy) } },
          });
    const items = (res.data as unknown as { cutOffTimes: CutOffTimeEntry[] }).cutOffTimes;
    const header = ["ID", "Time", "Use Case", "Days", "Department", "Task Type", "Default"];
    downloadCsv(
      target.kind === "store"
        ? `store-${target.storeName}-cut-off-times.csv`
        : `chain-${target.chainName}-cut-off-times.csv`,
      // Every chain row is chain-scoped, so the column would be a constant in that export.
      target.kind === "store" ? [...header, "Scope"] : header,
      items.map((c) => {
        const row = [
          String(c.cutOffTimeId ?? ""),
          formatCutOff(c.cutOffTime),
          c.useCase,
          c.dayOfWeek ?? "*",
          c.department ?? "",
          c.taskType ?? "",
          c.isDefault ? "Yes" : "No",
        ];
        return target.kind === "store" ? [...row, c.scope] : row;
      }),
    );
  }, [target, search, sortBy, orderBy, scope, useCase]);

  const columns = useMemo<DataTableColumnDef<CutOffTimeEntry>[]>(() => {
    const cols: DataTableColumnDef<CutOffTimeEntry>[] = [
      {
        accessorKey: "cutOffTimeId",
        header: t("stores.cutOffTimes.columns.id"),
        size: 80,
        enableHiding: false,
        meta: {
          columnLabel: t("stores.cutOffTimes.columns.id"),
          className: "pl-4",
          forceWidth: true,
          sticky: "left",
        },
        cell: ({ row }) => (row.original.cutOffTimeId != null ? <RowId id={row.original.cutOffTimeId} /> : <Dash />),
      },
      {
        accessorKey: "cutOffTime",
        header: t("stores.cutOffTimes.columns.cutOffTime"),
        size: 100,
        meta: { columnLabel: t("stores.cutOffTimes.columns.cutOffTime") },
        cell: ({ row }) => (
          <span className="font-mono text-sm tabular-nums">{formatCutOff(row.original.cutOffTime)}</span>
        ),
      },
      {
        accessorKey: "useCase",
        header: t("stores.cutOffTimes.columns.useCase"),
        meta: { columnLabel: t("stores.cutOffTimes.columns.useCase") },
        cell: ({ row }) => <UseCaseBadge useCase={row.original.useCase} />,
      },
      {
        accessorKey: "dayOfWeek",
        header: t("stores.cutOffTimes.columns.dayOfWeek"),
        meta: { columnLabel: t("stores.cutOffTimes.columns.dayOfWeek") },
        cell: ({ row }) => <DayOfWeekCell dayOfWeek={row.original.dayOfWeek} />,
      },
      {
        accessorKey: "department",
        header: t("stores.cutOffTimes.columns.department"),
        meta: { columnLabel: t("stores.cutOffTimes.columns.department") },
        cell: ({ row }) => row.original.department || <Dash />,
      },
      {
        accessorKey: "taskType",
        header: t("stores.cutOffTimes.columns.taskType"),
        meta: { columnLabel: t("stores.cutOffTimes.columns.taskType") },
        cell: ({ row }) => row.original.taskType || <Dash />,
      },
      {
        accessorKey: "isDefault",
        header: t("stores.cutOffTimes.columns.isDefault"),
        size: 90,
        meta: { columnLabel: t("stores.cutOffTimes.columns.isDefault") },
        cell: ({ row }) =>
          row.original.isDefault ? (
            <Badge variant="secondary" className="font-normal">
              {t("stores.cutOffTimes.default")}
            </Badge>
          ) : (
            <Dash />
          ),
      },
    ];
    // Only meaningful when the list can actually contain both kinds of row.
    if (showScope) {
      cols.push({
        accessorKey: "scope",
        header: t("stores.cutOffTimes.columns.scope"),
        size: 100,
        meta: { columnLabel: t("stores.cutOffTimes.columns.scope") },
        cell: ({ row }) => (
          <Badge variant="secondary" className={cn("font-normal", SCOPE_STYLES[row.original.scope])}>
            {t(`stores.cutOffTimes.scopes.${row.original.scope}`, { defaultValue: row.original.scope })}
          </Badge>
        ),
      });
    }
    if (canEditAnyRow) {
      cols.push({
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => {
          // A chain-wide row needs the chain grant even when reached from a store page, since it
          // is the default for every store in the chain. Kept visible-but-disabled with a reason
          // rather than hidden, so the restriction is discoverable.
          const isChainRow = row.original.scope === "chain";
          const editable = isChainRow ? canEditChainRows : canEditStoreRows;
          // Two different grants can be the missing one, so the reason has to follow the row's scope
          // — someone holding only the chain grant is blocked on the store's own rows, not the other
          // way round.
          const blockedHint = isChainRow
            ? t("stores.cutOffTimes.actions.noChainPermissionHint")
            : t("stores.cutOffTimes.actions.noStorePermissionHint");
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
                {!editable ? (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          aria-disabled
                          className="opacity-50 cursor-not-allowed"
                        >
                          <Pencil className="h-4 w-4" />
                          {t("stores.cutOffTimes.actions.edit")}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>{blockedHint}</TooltipContent>
                    </Tooltip>
                    <DropdownMenuSeparator />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuItem
                          onSelect={(e) => e.preventDefault()}
                          aria-disabled
                          variant="destructive"
                          className="opacity-50 cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4" />
                          {t("stores.cutOffTimes.actions.delete")}
                        </DropdownMenuItem>
                      </TooltipTrigger>
                      <TooltipContent>{blockedHint}</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onSelect={() => setEditing(row.original)}>
                      <Pencil className="h-4 w-4" />
                      {t("stores.cutOffTimes.actions.edit")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => setDeleting(row.original)} variant="destructive">
                      <Trash2 className="h-4 w-4" />
                      {t("stores.cutOffTimes.actions.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      });
    }
    return cols;
  }, [t, showScope, canEditAnyRow, canEditStoreRows, canEditChainRows]);

  return (
    <div className="p-6">
      <DataTable
        storageKey={isStore ? "store-cut-off-times" : "chain-cut-off-times"}
        columns={columns}
        data={data?.cutOffTimes ?? []}
        totalCount={data?.count ?? 0}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        search={search}
        onSearchChange={setSearch}
        searchDebounceMs={1000}
        onRefresh={refetch}
        isRefreshing={isFetching}
        onExport={handleExport}
        searchPlaceholder={t("stores.cutOffTimes.searchPlaceholder")}
        footerNote={t("stores.cutOffTimes.localTimeNote")}
        filters={
          <>
            <FilterSelect
              label={t("stores.cutOffTimes.filter.useCase")}
              value={useCase}
              onChange={(v) => {
                setUseCase(v);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              options={USE_CASES.map((u) => ({
                label: t(`stores.cutOffTimes.useCases.${u}`, { defaultValue: u }),
                value: u,
              }))}
              searchPlaceholder={t("stores.operations.searchOption")}
              emptyText={t("stores.operations.noOption")}
            />
            {showScope && (
              <FilterSelect
                label={t("stores.cutOffTimes.filter.scope")}
                value={scope}
                onChange={(v) => {
                  setScope(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={SCOPES.map((s) => ({
                  label: t(`stores.cutOffTimes.scopes.${s}`, { defaultValue: s }),
                  value: s,
                }))}
                searchPlaceholder={t("stores.operations.searchOption")}
                emptyText={t("stores.operations.noOption")}
              />
            )}
            {isFiltered && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearFilters}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        }
        toolbar={
          canCreate ? (
            <Button onClick={() => setCreateOpen(true)}>
              <ClockPlus className="h-4 w-4" />
              {t("stores.cutOffTimes.addButton")}
            </Button>
          ) : undefined
        }
      />

      <CreateCutOffTimeDialog target={target} open={createOpen} onOpenChange={setCreateOpen} />

      {/* Keyed on the row so the form re-seeds when a different row is picked without closing. */}
      <CreateCutOffTimeDialog
        key={`edit-${editing?.cutOffTimeId ?? "none"}`}
        target={target}
        editing={editing}
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />

      <DeleteCutOffTimeDialog target={target} entry={deleting} onClose={() => setDeleting(null)} />
    </div>
  );
}

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

function UseCaseBadge({ useCase }: { useCase: string }) {
  const { t } = useTranslation("back-office");
  return (
    <Badge variant="outline" className={cn("font-normal", USE_CASE_STYLES[useCase])}>
      {t(`stores.cutOffTimes.useCases.${useCase}`, { defaultValue: useCase })}
    </Badge>
  );
}

function DayOfWeekCell({ dayOfWeek }: { dayOfWeek?: string }) {
  const { t } = useTranslation("back-office");
  const days = parseDays(dayOfWeek);

  if (days.length === 0) {
    return <span className="text-muted-foreground">{t("stores.cutOffTimes.everyDay")}</span>;
  }

  return (
    <span className="flex flex-wrap gap-1">
      {days.map((day) => (
        <Badge key={day} variant="outline" className="font-normal">
          {t(`stores.cutOffTimes.days.${day.toLowerCase()}`, { defaultValue: day })}
        </Badge>
      ))}
    </span>
  );
}
